/**
 * Тримає дропдаун пошуку в хедері відкритим, поки по ньому тапають на iOS.
 *
 * Дропдаун відкритий лише доки форма `:focus-within`. iOS Safari при тапі
 * фокусує тільки поля форми: ні посилання, ні елемент із `tabindex` фокус
 * не отримують. Тап по картці знімає фокус з інпуту, форма гасне, дропдаун
 * ховається раніше, ніж спрацює `click`, і перехід на товар не відбувається.
 *
 * `pointerdown` приходить до втрати фокусу, тож тут форма отримує
 * `data-bk-pinned`, і CSS у header-custom.liquid тримає дропдаун по ньому.
 * Знімається тапом поза формою та при поверненні зі bfcache.
 */
const form = document.querySelector('.header-search__form');
const results = form?.querySelector('.header-search__results');

if (form && results) {
  results.addEventListener('pointerdown', () => form.setAttribute('data-bk-pinned', ''));

  document.addEventListener('pointerdown', (event) => {
    if (!form.contains(/** @type {Node} */ (event.target))) form.removeAttribute('data-bk-pinned');
  });

  window.addEventListener('pageshow', () => form.removeAttribute('data-bk-pinned'));
}

/**
 * Книги в наявності першими в підказках.
 *
 * API підказок віддає лише 10 перших за релевантністю товарів, а книги в
 * наявності (~4 % каталогу) у цю десятку часто не потрапляють зовсім. Тому
 * паралельно з підказками питаємо повний пошук із фільтром наявності (секція
 * bk-predictive-instock) і, коли predictive-search.js вставив свою розмітку,
 * ставимо ці картки на початок списку. Штатний predictive-search.js не змінюється:
 * момент вставки ловимо MutationObserver.
 */
const PRODUCTS_LIMIT = 8; // = limit у bk-predictive-products
const input = form?.querySelector('.header-search__input');
const scroll = form?.querySelector('.header-search__results-scroll');

if (form && input instanceof HTMLInputElement && scroll) {
  /** @type {Map<string, Promise<Element[]>>} */
  const cache = new Map();

  /** @param {string} term */
  const inStock = (term) => {
    let promise = cache.get(term);
    if (!promise) {
      const url = new URL(form.getAttribute('action') ?? '/search', location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'last');
      url.searchParams.set('filter.v.availability', '1');
      url.searchParams.set('section_id', 'bk-predictive-instock');
      promise = fetch(url)
        .then((response) => (response.ok ? response.text() : ''))
        .then((html) => Array.from(new DOMParser().parseFromString(html, 'text/html').querySelectorAll('li[data-product-id]')))
        .catch(() => []);
      cache.set(term, promise);
      if (cache.size > 20) cache.delete(cache.keys().next().value ?? '');
    }
    return promise;
  };

  /**
   * Книги, у назві яких є всі слова запиту, останнє — як початок слова.
   *
   * Ні підказки, ні повний пошук Shopify назву майже не цінують: на «Бог завжди»
   * підказки не повертають саму книгу «Бог завжди подорожує інкогніто» серед
   * 10, а повний пошук ставить її 51-ю. Тому збіг шукаємо самі, в індексі
   * каталогу (той самий, що в bkv-catalog.js; на сторінці пошуку він уже
   * завантажений), а картки беремо в секції bk-predictive-instock за id.
   * Індекс (~700 КБ) вантажимо лише після першого набраного запиту.
   */
  /** @type {Promise<{ index: any, text: any } | null> | undefined} */
  let catalog;
  const loadCatalog = () => {
    const started = (/** @type {any} */ (window).bkvCatalog ??= {});
    const load = (/** @type {string} */ key, /** @type {string | undefined} */ url) =>
      (started[key] ??= fetch(url ?? '').then((response) => {
        if (!response.ok) throw new Error(`${key} HTTP ${response.status}`);
        return response.json();
      }));
    return (catalog ??= Promise.all([load('index', form.dataset.bkIndexUrl), load('text', form.dataset.bkTextUrl)])
      .then(([index, text]) => (index.v === 1 && text.count === index.items.id.length ? { index, text } : null))
      .catch(() => null));
  };

  /** @param {string} term */
  const byTitle = (term) => {
    const key = `title:${term}`;
    let promise = cache.get(key);
    if (!promise) {
      promise = loadCatalog().then(async (data) => {
        const tokens = normalizeText(term).split(' ').filter(Boolean);
        if (!data || tokens.join('').length < 3) return [];
        const { items } = data.index;
        /** @type {number[]} */
        const hits = [];
        for (let i = 0; i < data.text.count; i++) {
          const haystack = ' ' + data.text.text[i];
          const title = haystack.slice(0, haystack.indexOf(' | '));
          if (tokens.every((t) => title.includes(' ' + t))) hits.push(i);
        }
        if (hits.length === 0) return [];
        hits.sort((a, b) => items.available[b] - items.available[a]);
        const ids = hits.slice(0, PRODUCTS_LIMIT).map((i) => String(items.id[i]));

        const url = new URL(form.getAttribute('action') ?? '/search', location.origin);
        url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
        url.searchParams.set('type', 'product');
        url.searchParams.set('section_id', 'bk-predictive-instock');
        const response = await fetch(url);
        if (!response.ok) return [];
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const cards = new Map(
          Array.from(doc.querySelectorAll('li[data-product-id]')).map((li) => [/** @type {HTMLElement} */ (li).dataset.productId, li])
        );
        return ids.flatMap((id) => cards.get(id) ?? []);
      }).catch(() => []);
      cache.set(key, promise);
      if (cache.size > 40) cache.delete(cache.keys().next().value ?? '');
    }
    return promise;
  };

  // Запит стартує з набором тексту, а не після підказок: так обидва йдуть паралельно.
  let timer = 0;
  input.addEventListener('input', () => {
    clearTimeout(timer);
    const term = input.value.trim();
    if (term) {
      timer = window.setTimeout(() => {
        inStock(term);
        byTitle(term);
      }, 150);
    }
  });

  const merge = async () => {
    const term = input.value.trim();
    const list = scroll.querySelector('#predictive-search-products ul');
    if (!term || !(list instanceof HTMLElement) || list.dataset.bkMerged === term) return;
    list.dataset.bkMerged = term;

    const [titled, available] = await Promise.all([byTitle(term), inStock(term)]);
    // Поки чекали, користувач міг дописати запит — тоді список уже чужий.
    if (!list.isConnected || input.value.trim() !== term) return;

    // Збіг назви — першим, навіть розпроданий: покупець має знайти саме ту
    // книгу, яку шукає, і підписатися на неї. Далі книги в наявності.
    const titledIds = new Set(titled.map((li) => /** @type {HTMLElement} */ (li).dataset.productId));
    const found = [...titled, ...available.filter((li) => !titledIds.has(/** @type {HTMLElement} */ (li).dataset.productId))];
    if (found.length === 0) return;

    // Показані картки не переставляємо: вийнята й вставлена назад картка для
    // браузера нова, і анімація появлення програється вдруге. Лише прибираємо
    // дублі, додаємо нові на початок і зрізаємо хвіст.
    const ids = new Set(found.map((li) => /** @type {HTMLElement} */ (li).dataset.productId));
    for (const li of Array.from(list.children)) {
      if (ids.has(/** @type {HTMLElement} */ (li).dataset.productId)) li.remove();
    }
    list.prepend(...found.map((li) => document.importNode(li, true)));
    while (list.children.length > PRODUCTS_LIMIT) list.lastElementChild?.remove();
    list.dataset.bkMerged = term;
  };

  new MutationObserver(() => merge()).observe(scroll, { childList: true, subtree: true });
}

/**
 * ⚠ Копія `normalizeText` з bkv-catalog.js і local_scripts/bk-catalog-index.mjs —
 * запит і індекс мають нормалізуватися однаково, змінювати всі три разом.
 * @param {string} value
 */
function normalizeText(value) {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
}
